import {useConfig} from '@aboutyou/config';
import {CouponInfoSheet} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {GetWalletResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wallet/v1/page/service.types';
import {FadeTransition} from '@aboutyou/transitions';
import {
  COLORS,
  ContentPopper,
  DrawerBody,
  DrawerHeader,
  Flyout,
  ModalDialog,
  ModalHeader,
  SPACING,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import coinsWalletBackground from '../../features/Coins/assets/background-dark.png';
import {RequestFailure} from '../../features/Coins/shared/RequestFailure';
import {CouponInfoContentPopper} from '../../features/Coupons/CouponInfoContentPopper/CouponInfoContentPopper';
import {COUPON_WALLET_INFO_ICON_CUSTOM_KEY} from '../../features/Coupons/CouponWallet/deprecated/highTierWallet/WalletBodyContentView';
import couponWalletBackground from '../../features/Coupons/assets/couponWalletBackground.svg';
import {WalletContentSkeletons} from './Skeletons/WalletContentSkeletons';
import {WalletHeaderSkeletons} from './Skeletons/WalletHeaderSkeletons';
import {WalletProps} from './Wallet';
import {WalletContent} from './WalletContent';
import {WalletHeader} from './WalletHeader';
import {ConditionalWalletPauseState} from './WalletPauseState';
import {WalletType, useActiveTab} from './utils/useActiveTab';
import {useIsCurrentTabErrored} from './utils/useIsCurrentTabErrored';
import {useNormalizeTabs} from './utils/useNormalizeTabs';
import {useTrackWalletContentView} from './utils/useTrackWalletContentView';
import {useWallet} from './utils/useWallet';

export const WalletView: React.FC<
  WalletProps & {
    tabs: GetWalletResponse['tabs'];
    requestError?: boolean;
    preventAnimation?: boolean;
  }
> = ({
  ContentPopperLayoutComponent,
  setSelectedProduct,
  tabs,
  requestError = false,
  preventAnimation = false,
}) => {
  const {device} = useConfig();
  const onCloseInfoSheet = useRef<VoidFunction>(undefined);
  const [infoIconContent, setInfoIconContent] = useState<CouponInfoSheet>();
  const [hasScrolled, setHasScrolled] = useState(false);
  const {activeTab, onTabClick, walletContentRef} = useActiveTab();
  const {handleOnClose} = useWallet();
  const normalizedTabs = useNormalizeTabs(tabs);

  useTrackWalletContentView();

  const hasError =
    useIsCurrentTabErrored(activeTab, normalizedTabs) || requestError;

  const showSolidHeader = hasScrolled || hasError;

  const handleCouponInfoIconClick = useCallback(
    (couponInfo?: CouponInfoSheet) => {
      setInfoIconContent(couponInfo);
    },
    [],
  );

  const headerContent = useMemo(() => {
    if (requestError) {
      return null;
    }

    if (tabs.length === 0) {
      return <WalletHeaderSkeletons activeTab={activeTab} />;
    }

    return (
      <WalletHeader
        showSolidHeader={showSolidHeader}
        tabs={normalizedTabs}
        activeTab={activeTab}
        onTabClick={onTabClick}
      />
    );
  }, [
    requestError,
    tabs,
    showSolidHeader,
    normalizedTabs,
    activeTab,
    onTabClick,
  ]);

  const bodyContent = useMemo(() => {
    if (hasError) {
      return <StyledRequestFailure />;
    }

    if (tabs.length === 0) {
      return (
        <>
          <Background data-tab={activeTab} $isLoading />
          <WalletContentSkeletons activeTab={activeTab} />
        </>
      );
    }

    return (
      <>
        <Background data-tab={activeTab} />
        <StyledFadeTransition duration={200} uniqueKey={activeTab}>
          <Body data-testid="wallet-content" ref={walletContentRef}>
            <WalletContent
              activeTab={activeTab}
              tabs={normalizedTabs}
              setSelectedProduct={setSelectedProduct}
              handleCouponInfoIconClick={handleCouponInfoIconClick}
              setHasScrolled={setHasScrolled}
            />
          </Body>
        </StyledFadeTransition>
      </>
    );
  }, [
    hasError,
    tabs,
    activeTab,
    walletContentRef,
    normalizedTabs,
    setSelectedProduct,
    handleCouponInfoIconClick,
  ]);

  return (
    <>
      <StyledContentPopper
        data-testid="wallet"
        data-solid-header={showSolidHeader}
        $activeTab={activeTab}
        $isLoading={tabs.length === 0}
        renderInPortal
        preventAnimation={preventAnimation}
        slideDirection="left"
        LayoutComponent={ContentPopperLayoutComponent}
        onClose={handleOnClose}
        onGoBack={device === 'mobile' ? handleOnClose : undefined}
        open={true}
        preventSlideUpAnimation
        headerContent={headerContent}
        bodyContent={bodyContent}
      />

      <ConditionalWalletPauseState
        activeTab={activeTab}
        tabs={normalizedTabs}
      />

      <StyledCouponInfoContentPopper
        LayoutComponent={process.env.STACK === 'mobile' ? Flyout : ModalDialog}
        data-testid="couponCard"
        couponInfo={infoIconContent}
        stateManagerCustomKey={COUPON_WALLET_INFO_ICON_CUSTOM_KEY}
        onClose={onCloseInfoSheet.current}
      />
    </>
  );
};

const StyledContentPopper = styled(ContentPopper)<{
  $activeTab: WalletType;
  $isLoading?: boolean;
}>`
  --header-color: ${props =>
    props.$activeTab === 'notificationCenter'
      ? COLORS['Text.Black']
      : COLORS['Text.White']};
  --header-background-color: ${props =>
    props.$activeTab === 'notificationCenter' && !props.$isLoading
      ? COLORS['Fill.Grey.Primary']
      : 'transparent'};

  ${DrawerHeader}, ${ModalHeader} {
    transition: background-color 0.3s, color 0.3s;
    color: var(--header-color);
    background-color: var(--header-background-color);
    z-index: 1;

    svg {
      fill: var(--header-color);
    }
  }

  &[data-solid-header='true'] {
    --header-color: ${COLORS['Text.Black']};
    --header-background-color: ${COLORS['Fill.White']};
  }

  ${ModalHeader} {
    padding-inline: ${SPACING['X.Lg']};
  }

  ${DrawerBody} {
    padding: 0;
    scrollbar-width: none;
    border-top: none;
  }
`;

const StyledFadeTransition = styled(FadeTransition)`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const Background = styled.div<{$isLoading?: boolean}>`
  position: absolute;
  inset: 0;
  background-size: cover;
  opacity: 0;
  transition: background-image 0s, background-color 0s, opacity 0.3s;

  &[data-tab='coins'] {
    background-image: url(${coinsWalletBackground});
    opacity: 1;
  }

  &[data-tab='coupons'] {
    background-image: url(${couponWalletBackground});
    background-color: ${COLORS['Coupon.Primary']};
    opacity: 1;
  }

  &[data-tab='notificationCenter'] {
    background: ${props =>
      props.$isLoading ? COLORS['Fill.White'] : COLORS['Fill.Grey.Primary']};
    opacity: 1;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  /* x is clipped, not auto: scrollbars are hidden below, so horizontal
     overflow from any child would be invisible on desktop but leave the
     whole wallet pannable sideways on touch devices (QA: iOS). */
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
`;

const StyledCouponInfoContentPopper = styled(CouponInfoContentPopper)`
  &&& {
    z-index: ${Z_INDEXES.FLYOUT_FADE_ON_DRAWER};
  }
`;

const StyledRequestFailure = styled(RequestFailure)`
  padding-top: 8rem;
  height: 100%;
  background-color: ${COLORS['Fill.Grey.Primary']};
`;
